import React, { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  Send,
  Edit,
  Printer,
  CreditCard,
  CheckCircle2,
  Clock,
  AlertCircle,
  MoreVertical,
  FileText,
  Trash2,
  Eye,
  DollarSign,
  Share2
} from 'lucide-react'
import { useApp } from '../context/AppContext'
import { generateInvoicePDF, downloadInvoicePDF, printInvoicePDF, downloadReceiptPDF } from '../services/pdfService'
import { emailService } from '../services/emailService'

export default function InvoiceView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    getInvoiceById,
    getClientById,
    settings,
    sendInvoice,
    markAsPaid,
    deleteInvoice,
    addNotification
  } = useApp()

  const invoice = getInvoiceById(id)
  const client = invoice ? getClientById(invoice.clientId) : null
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showActions, setShowActions] = useState(false)

  if (!invoice) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <FileText size={64} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Invoice Not Found</h2>
        <p className="text-gray-500 mt-2">The invoice you're looking for doesn't exist.</p>
        <Link to="/invoices" className="btn btn-primary mt-6 inline-flex">
          <ArrowLeft size={18} />
          Back to Invoices
        </Link>
      </div>
    )
  }

  const getStatusConfig = (status) => {
    const configs = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText, label: 'Draft' },
      sent: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Sent' },
      viewed: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: Eye, label: 'Viewed' },
      partial: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Partial Payment' },
      paid: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Paid' },
      overdue: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle, label: 'Overdue' },
    }
    return configs[status] || configs.draft
  }

  const statusConfig = getStatusConfig(invoice.status)
  const StatusIcon = statusConfig.icon

  const handleSend = async () => {
    // Check if email is configured
    if (!settings.emailServiceId || !settings.emailTemplateId) {
      if (window.confirm('Email settings are not configured. Would you like to go to settings now?')) {
        navigate('/settings')
      }
      return
    }

    try {
      addNotification('Sending invoice...', 'info')
      const link = `${window.location.origin}/portal/${invoice.id}`
      await emailService.sendInvoiceEmail(invoice, client, settings, link)

      await sendInvoice(id)
      addNotification('Invoice sent successfully', 'success')
      setShowActions(false)
    } catch (error) {
      addNotification('Failed to send email. Check your settings.', 'error')
    }
  }

  // Handle WhatsApp sharing
  const handleWhatsApp = async () => {
    // Use receipt link if paid, otherwise invoice link
    const link = invoice.status === 'paid'
      ? `${window.location.origin}/portal/${invoice.id}/receipt`
      : `${window.location.origin}/portal/${invoice.id}`

    const formattedAmount = invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const dueDate = new Date(invoice.dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    const subjectLine = invoice.subject ? `Subject: ${invoice.subject}` : ''

    const clientDisplay = client.businessName || client.name
    let message = ''

    if (invoice.status === 'paid') {
      message = `Hi ${clientDisplay},

Thank you for your payment! 
We have received payment in full for Invoice ${invoice.invoiceNumber}.
${subjectLine}

You can view your receipt here:
${link}

Thank you for your business!`
    } else if (invoice.status === 'overdue') {
      message = `Hi ${clientDisplay},

Friendly reminder that Invoice ${invoice.invoiceNumber} is overdue.
${subjectLine}

Amount Due: $${formattedAmount}
Due Date: ${dueDate}

Please make payment securely here:
${link}`
    } else if (invoice.status === 'partial') {
      const balanceDue = (invoice.total - (invoice.partialAmount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      message = `Hi ${clientDisplay},

Thank you for your partial payment on Invoice ${invoice.invoiceNumber}.
${subjectLine}

Remaining Balance: $${balanceDue}
Due Date: ${dueDate}

You can view the invoice and pay the balance here:
${link}`
    } else {
      // Default / Sent / Draft
      message = `Hi ${clientDisplay},

${settings.businessName} has sent you Invoice ${invoice.invoiceNumber}.
${subjectLine}

Amount Due: $${formattedAmount}
Due Date: ${dueDate}

You can view and pay securely online here:
${link}

Thank you!`
    }

    const encodedMessage = encodeURIComponent(message)
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`

    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank')

    // Update invoice status if it was draft
    if (invoice.status === 'draft') {
      await sendInvoice(id)
    }

    addNotification('WhatsApp opened', 'success')
    setShowActions(false)
  }

  const handleDownloadReceipt = () => {
    const lastPaymentAmount = invoice.payments && invoice.payments.length > 0
      ? invoice.payments[invoice.payments.length - 1].amount
      : invoice.partialAmount || invoice.total

    downloadReceiptPDF(invoice, client, settings, lastPaymentAmount)
    addNotification('Receipt downloading...', 'success')
    setShowActions(false)
  }

  const handleMarkPaid = async () => {
    await markAsPaid(id)
    setShowActions(false)
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      deleteInvoice(id)
      addNotification('Invoice deleted', 'success')
      navigate('/invoices')
    }
    setShowActions(false)
  }

  const handleDownloadPDF = () => {
    try {
      downloadInvoicePDF(invoice, client, settings)
      addNotification('PDF downloaded successfully', 'success')
    } catch (error) {
      addNotification('Failed to generate PDF', 'error')
    }
    setShowActions(false)
  }

  const handlePrint = () => {
    try {
      printInvoicePDF(invoice, client, settings)
      addNotification('Print dialog opened', 'info')
    } catch (error) {
      addNotification('Failed to generate PDF for printing', 'error')
    }
    setShowActions(false)
  }

  const isOverdue = invoice.status === 'overdue' ||
    (invoice.status !== 'paid' && new Date(invoice.dueDate) < new Date())

  // Helper to safely format dates handling both Firestore Timestamps and strings
  const formatDateTime = (dateValue) => {
    if (!dateValue) return ''
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue)
    return date.toLocaleString()
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in pb-24 md:pb-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/invoices"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors btn-touch"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </Link>

          <div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{invoice.invoiceNumber}</h2>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                  <StatusIcon size={14} />
                  {statusConfig.label}
                </span>
              </div>
              {invoice.subject && (
                <p className="text-lg md:text-xl font-bold text-gray-800">{invoice.subject}</p>
              )}
            </div>
            <p className="text-gray-500 mt-1 text-sm md:text-base">
              Issued {new Date(invoice.issueDate).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric'
              })}
            </p>
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3 flex-wrap justify-end">
          <button
            onClick={handleWhatsApp}
            className="btn btn-primary flex-1 sm:flex-initial"
          >
            <Share2 size={18} />
            <span>Send via WhatsApp</span>
          </button>

          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="btn btn-secondary"
            >
              <MoreVertical size={18} />
              <span className="hidden sm:inline">Actions</span>
            </button>
            {showActions && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20 animate-scale-in">
                  <Link
                    to={`/invoices/${id}/edit`}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Edit size={16} />
                    Edit Invoice
                  </Link>
                  <button
                    onClick={handleDownloadPDF}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                  >
                    <Download size={16} />
                    Download PDF
                  </button>
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                  >
                    <Printer size={16} />
                    Print
                  </button>
                  {['draft', 'sent', 'viewed', 'partial'].includes(invoice.status) && (
                    <button
                      onClick={handleSend}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                    >
                      <Send size={16} />
                      Send via Email
                    </button>
                  )}
                  {/* Share on WhatsApp is now a main button, but keeping it here as secondary option too */}
                  <button
                    onClick={handleWhatsApp}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full sm:hidden"
                  >
                    <Share2 size={16} />
                    Share on WhatsApp
                  </button>
                  <hr className="my-1" />
                  {['sent', 'viewed', 'partial'].includes(invoice.status) && invoice.status !== 'paid' && (
                    <button
                      onClick={handleMarkPaid}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50 w-full"
                    >
                      <CheckCircle2 size={16} />
                      Mark as Paid
                    </button>
                  )}
                  {['partial', 'paid'].includes(invoice.status) && (
                    <button
                      onClick={handleDownloadReceipt}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                    >
                      <FileText size={16} />
                      Download Receipt
                    </button>
                  )}
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>

          {['sent', 'viewed', 'partial'].includes(invoice.status) && invoice.status !== 'paid' && (
            <button
              onClick={() => setShowPaymentModal(true)}
              className="btn btn-success hidden md:flex"
            >
              <CreditCard size={18} />
              Record Payment
            </button>
          )}
        </div>
      </div>

      {/* Invoice Content */}
      <div className="card">
        {/* Invoice Header */}
        <div className="p-8 border-b border-gray-100">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4">
              {settings?.logo ? (
                <img
                  src={settings.logo}
                  alt="Business Logo"
                  className="w-20 h-20 object-contain"
                />
              ) : (
                <img
                  src="/ncinq-favicon.png"
                  alt="nCinq Invoice Logo"
                  className="w-14 h-14 object-contain"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">INVOICE</h1>
              </div>
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-900 text-lg">{settings.businessName}</p>
              <p className="text-gray-500 whitespace-pre-line mt-1">{settings.businessAddress}</p>
              <p className="text-gray-500">{settings.businessEmail}</p>
            </div>
          </div>
        </div>

        {/* Client & Date Info */}
        <div className="p-8 bg-gray-50 border-b border-gray-100">
          <div className="flex flex-wrap justify-between gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1 font-medium">Bill To</p>
              {client?.businessName && (
                <p className="font-bold text-gray-900 text-lg">{client.businessName}</p>
              )}
              <p className={`${client?.businessName ? 'font-medium text-gray-700' : 'font-semibold text-gray-900 text-lg'}`}>
                {client?.name}
              </p>
              <p className="text-gray-600 whitespace-pre-line mt-1">{client?.address}</p>
              <p className="text-sm text-gray-500 mt-1">{client?.email}</p>
            </div>
            <div className="text-right">
              <div className="mb-3">
                <p className="text-sm text-gray-500">Invoice Number</p>
                <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
              </div>
              <div className="mb-3">
                <p className="text-sm text-gray-500">Issue Date</p>
                <p className="font-medium text-gray-900">
                  {new Date(invoice.issueDate).toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric'
                  })}
                </p>
              </div>
              <div className={isOverdue ? 'text-red-600' : ''}>
                <p className="text-sm text-gray-500">Due Date</p>
                <p className="font-medium">
                  {new Date(invoice.dueDate).toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric'
                  })}
                  {isOverdue && (
                    <span className="ml-2 text-red-600 font-semibold">(Overdue)</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="p-8">
          <table className="w-full mb-8">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 text-sm font-semibold text-gray-600">Description</th>
                <th className="text-center py-3 text-sm font-semibold text-gray-600 w-24">Qty</th>
                <th className="text-right py-3 text-sm font-semibold text-gray-600 w-28">Rate</th>
                <th className="text-right py-3 text-sm font-semibold text-gray-600 w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-4 text-gray-900">{item.description}</td>
                  <td className="py-4 text-center text-gray-600">{item.quantity}</td>
                  <td className="py-4 text-right text-gray-600">${item.rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="py-4 text-right font-medium text-gray-900">
                    ${(item.quantity * item.rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900">${invoice.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {invoice.taxRate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{invoice.taxLabel} ({invoice.taxRate}%)</span>
                  <span className="text-gray-900">${invoice.taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              {invoice.discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Discount</span>
                  <span className="text-green-600">-${invoice.discount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t-2 border-gray-200 pt-3">
                <span>Total</span>
                <span className="text-primary-600">${invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {settings.enableExchange && settings.exchangeRate > 0 && (
                <div className="flex justify-between text-sm italic text-gray-500 pt-1">
                  <span>Equivalent in {settings.exchangeCurrency || 'GYD'}</span>
                  <span>
                    {(invoice.total * settings.exchangeRate).toLocaleString('en-US', {
                      style: 'currency',
                      currency: settings.exchangeCurrency || 'GYD'
                    })}
                  </span>
                </div>
              )}
              {/* Payment History */}
              {invoice.payments && invoice.payments.length > 0 ? (
                <div className="pt-3 border-t border-gray-100 space-y-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Payment History</p>
                  {invoice.payments && [...invoice.payments].sort((a, b) => {
                    const dateA = a.date && (a.date.toDate ? a.date.toDate() : new Date(a.date))
                    const dateB = b.date && (b.date.toDate ? b.date.toDate() : new Date(b.date))
                    return dateB - dateA
                  }).map((payment, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-gray-500">
                        Paid on {payment.date && (payment.date.toDate ? payment.date.toDate() : new Date(payment.date)).toLocaleDateString()}
                      </span>
                      <span className="text-green-600 font-medium">-${Number(payment.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-50">
                    <span className="text-gray-700">Total Paid</span>
                    <span className="text-green-600">-${(invoice.partialAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              ) : invoice.partialAmount && (
                <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
                  <span className="text-gray-500">Paid</span>
                  <span className="text-green-600">-${invoice.partialAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              {invoice.status === 'partial' && (
                <div className="flex justify-between text-lg font-bold border-t-2 border-gray-200 pt-3">
                  <span>Balance Due</span>
                  <span className="text-primary-600">
                    ${(invoice.total - invoice.partialAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Notes & Terms */}
          {(invoice.notes || invoice.terms) && (
            <div className="border-t border-gray-200 pt-6 space-y-4">
              {invoice.notes && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                  <p className="text-sm text-gray-600">{invoice.notes}</p>
                </div>
              )}
              {invoice.terms && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Terms</p>
                  <p className="text-sm text-gray-600">{invoice.terms}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Activity</h3>
        <div className="space-y-4">
          {invoice.createdAt && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <FileText size={14} className="text-primary-600" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Invoice created</p>
                <p className="text-xs text-gray-500">
                  {formatDateTime(invoice.createdAt)}
                </p>
              </div>
            </div>
          )}
          {invoice.sentAt && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Send size={14} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Invoice sent to {client?.email}</p>
                <p className="text-xs text-gray-500">
                  {formatDateTime(invoice.sentAt)}
                </p>
              </div>
            </div>
          )}
          {invoice.paidAt && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={14} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Payment received</p>
                <p className="text-xs text-gray-500">
                  {formatDateTime(invoice.paidAt)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          invoice={invoice}
          onClose={() => setShowPaymentModal(false)}
        />
      )}
    </div>
  )
}

function PaymentModal({ invoice, onClose }) {
  const { recordPayment, addNotification } = useApp()
  const [amount, setAmount] = useState(invoice.partialAmount ? (invoice.total - invoice.partialAmount).toFixed(2) : invoice.total.toFixed(2))
  const [processing, setProcessing] = useState(false)

  const handlePayment = () => {
    setProcessing(true)
    setTimeout(() => {
      recordPayment(invoice.id, parseFloat(amount))
      addNotification('Payment recorded successfully', 'success')
      onClose()
    }, 1000)
  }

  const remainingBalance = invoice.total - (invoice.partialAmount || 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-in">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Record Payment</h3>
          <p className="text-sm text-gray-500 mt-1">Enter the payment amount for {invoice.invoiceNumber}</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">Invoice Total</span>
              <span className="font-medium text-gray-900">${invoice.total.toFixed(2)}</span>
            </div>
            {invoice.partialAmount && (
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">Already Paid</span>
                <span className="text-green-600">-${invoice.partialAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-2">
              <span className="text-gray-900">Balance Due</span>
              <span className="text-primary-600">${remainingBalance.toFixed(2)}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Amount
            </label>
            <div className="relative">
              <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                max={remainingBalance}
                min="0"
                step="0.01"
                className="input pl-10 text-lg font-semibold"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => setAmount(remainingBalance.toFixed(2))}
              className="flex-1 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Pay Full Amount
            </button>
            <button
              onClick={() => setAmount((remainingBalance / 2).toFixed(2))}
              className="flex-1 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              50%
            </button>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={processing || !amount || parseFloat(amount) <= 0}
            className="flex-1 btn btn-success"
          >
            {processing ? 'Processing...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  )
}
